import React, { ReactNode, useEffect, useState, useCallback } from "react";
import { useTranslation as useI18nextTranslation, I18nextProvider } from "react-i18next";
import i18n from "../i18n/i18n";
import { SUPPORTED_LANGUAGES, LanguageCode, LanguageInfo } from "./translations";
import {
  getLanguagePreference,
  saveLanguagePreference,
  resolveEffectiveLanguage,
  applyDocumentLanguage,
  parseRouteLanguage,
  buildLanguageUrl,
  detectBrowserLanguage,
  LanguagePreference,
} from "./languageRegistry";

export interface I18nProviderProps {
  children: ReactNode;
  initialUserLanguage?: string;
  onLanguageSyncedToServer?: (lang: string) => Promise<void>;
}

export function I18nProvider({ children, initialUserLanguage, onLanguageSyncedToServer }: I18nProviderProps) {
  // Sync state if initialUserLanguage arrives (e.g. after database loads)
  useEffect(() => {
    if (initialUserLanguage) {
      saveLanguagePreference(initialUserLanguage);
      const effective = resolveEffectiveLanguage(window.location.pathname, initialUserLanguage);
      if (i18n.language !== effective) {
        i18n.changeLanguage(effective);
        applyDocumentLanguage(effective);
      }
    }
  }, [initialUserLanguage]);

  // Keep language in sync with route changes (e.g. /es/ -> /fr/)
  useEffect(() => {
    const handleLocationChange = () => {
      const effective = resolveEffectiveLanguage(window.location.pathname);
      if (i18n.language !== effective) {
        i18n.changeLanguage(effective);
        applyDocumentLanguage(effective);
      }
    };

    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  // Handle language change sync to server
  useEffect(() => {
    const handleLangChanged = (lang: string) => {
      applyDocumentLanguage(lang);
      if (onLanguageSyncedToServer) {
        onLanguageSyncedToServer(lang).catch((err) => {
          console.warn("Failed to sync updated language setting to server database: ", err);
        });
      }
    };
    i18n.on("languageChanged", handleLangChanged);
    return () => {
      i18n.off("languageChanged", handleLangChanged);
    };
  }, [onLanguageSyncedToServer]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export function useTranslation(ns?: string | string[]) {
  const { t: i18nT, i18n: i18nInstance } = useI18nextTranslation(ns);

  const activeLang = (i18nInstance.language || "en") as LanguageCode;
  const locale = (SUPPORTED_LANGUAGES.some((l) => l.code === activeLang) ? activeLang : "en") as LanguageCode;
  const currentLanguagePreference = getLanguagePreference();
  
  const currentLanguageInfo: LanguageInfo = SUPPORTED_LANGUAGES.find((l) => l.code === locale) || {
    code: "en",
    name: "English",
    nativeName: "English",
  };
  const dir = currentLanguageInfo.isRTL ? "rtl" : "ltr";

  // Provide local missing keys tracking
  const [missingKeys, setMissingKeys] = useState<string[]>([]);

  useEffect(() => {
    const handleMissingKey = (lngs: readonly string[], namespace: string, key: string) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[I18N MISSING] ${lngs?.[0] || activeLang}: ${namespace}:${key}`);
      }
      setMissingKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };
    i18nInstance.on("missingKey", handleMissingKey);
    return () => {
      i18nInstance.off("missingKey", handleMissingKey);
    };
  }, [i18nInstance, activeLang]);

  const clearMissingKeys = () => setMissingKeys([]);

  const changeLanguage = useCallback(
    async (pref: LanguagePreference | LanguageCode) => {
      saveLanguagePreference(pref);
      const effectiveLang = resolveEffectiveLanguage(window.location.pathname, pref);
      await i18nInstance.changeLanguage(effectiveLang);
      applyDocumentLanguage(effectiveLang);

      // If user is on a public route, update URL prefix smoothly if needed
      const { routeWithoutLang, hasLangPrefix } = parseRouteLanguage(window.location.pathname);
      if (hasLangPrefix || (pref !== "auto" && pref !== "en")) {
        const targetUrl = buildLanguageUrl(routeWithoutLang, effectiveLang);
        if (window.location.pathname !== targetUrl) {
          window.history.pushState({}, "", targetUrl);
          window.dispatchEvent(new Event("popstate"));
        }
      }
    },
    [i18nInstance]
  );

  // Translation helper function with safe fallback
  const t = useCallback(
    (key: string, ...args: any[]): string => {
      if (!key) return "";
      const result = (i18nT as any)(key, ...args);
      if (result === undefined || result === null || result === "") {
        return key;
      }
      return String(result);
    },
    [i18nT]
  );

  // Intelligent Formatting via Intl Browser APIs
  const formatDate = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === "string" ? new Date(date) : date;
      if (!d || isNaN(d.getTime())) return String(date || "");

      const defaultOptions: Intl.DateTimeFormatOptions = options || {
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      try {
        return new Intl.DateTimeFormat(locale, defaultOptions).format(d);
      } catch {
        return d.toLocaleDateString();
      }
    },
    [locale]
  );

  const formatNumber = useCallback(
    (num: number, options?: Intl.NumberFormatOptions): string => {
      if (typeof num !== "number" || isNaN(num)) return "0";
      try {
        return new Intl.NumberFormat(locale, options).format(num);
      } catch {
        return num.toLocaleString();
      }
    },
    [locale]
  );

  const formatCurrency = useCallback(
    (amount: number, currency: string = "USD"): string => {
      if (typeof amount !== "number" || isNaN(amount)) return "$0";
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return `$${amount}`;
      }
    },
    [locale]
  );

  return {
    locale,
    languagePreference: currentLanguagePreference,
    currentLanguageInfo,
    languages: SUPPORTED_LANGUAGES,
    dir,
    t,
    changeLanguage,
    missingKeys,
    clearMissingKeys,
    formatDate,
    formatNumber,
    formatCurrency,
    i18n: i18nInstance,
  };
}

export type { LanguageCode, LanguageInfo, LanguagePreference };
export { SUPPORTED_LANGUAGES };
